// ───────────────────── markdown import view ─────────────────────
//
// Full-screen import flow (compare-mode pattern): pick a theme, tweak
// any style the markdown format can carry, watch the real preview —
// every page shown is a render of the ACTUAL generated PDF, so
// confirming locks in exactly what's on screen. Rebuilds are debounced
// engine round-trips (parse happened once, on mount); the worker keeps
// the generated document as scratch state until commit/cancel.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { marked, type Token, type Tokens } from "marked";
import type { CustomFont } from "../lib/fonts.ts";
import {
  MD_THEMES,
  cloneTheme,
  type MdHeadingStyle,
  type MdThemeConfig,
} from "../lib/md-theme.ts";
import type {
  MdFace,
  MdImageResolved,
  PageInfo,
  RenderResult,
} from "../lib/protocol.ts";
import { MM_TO_PT, PAGE_PRESETS } from "../lib/ui-constants.ts";
import { ColorPicker } from "./ColorPicker.tsx";
import {
  AlignSelect,
  FontSelect,
  Num,
  PreviewPage,
  Row,
  Section,
  StyleToggles,
  familyCss,
  fieldCls,
  resolveFaces,
} from "./import-shared.tsx";

const THEME_STORE_KEY = "pdfEditorMdImportTheme";
const MAX_IMAGES = 16;

/** Every family the theme can draw with. */
function themeFamilies(c: MdThemeConfig): string[] {
  const fams = new Set<string>([c.body.family, c.code.family, c.codespan.family]);
  for (const h of c.headings) fams.add(h.family);
  return [...fams];
}

// ───────────────────────── image resolution ─────────────────────────

function collectImageHrefs(tokens: Token[], out: Set<string>): void {
  for (const t of tokens) {
    const tok = t as Tokens.Generic;
    if (tok.type === "image" && typeof tok.href === "string") out.add(tok.href);
    if (Array.isArray(tok.tokens)) collectImageHrefs(tok.tokens as Token[], out);
    if (Array.isArray(tok.items)) {
      for (const item of tok.items as Tokens.ListItem[]) {
        collectImageHrefs(item.tokens ?? [], out);
      }
    }
    if (Array.isArray(tok.header)) {
      for (const cell of tok.header as Tokens.TableCell[]) collectImageHrefs(cell.tokens, out);
      for (const row of (tok.rows ?? []) as Tokens.TableCell[][]) {
        for (const cell of row) collectImageHrefs(cell.tokens, out);
      }
    }
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("read failed"));
    r.readAsDataURL(blob);
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("decode failed"));
    img.src = dataUrl;
  });
}

/** data/remote URL → PNG-or-JPEG data URL + natural size. Formats
 *  mupdf can't ingest (webp, svg, …) are re-encoded through a canvas. */
async function resolveImage(href: string): Promise<MdImageResolved | null> {
  let dataUrl: string;
  if (href.startsWith("data:")) {
    dataUrl = href;
  } else if (/^https?:\/\//i.test(href)) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 5000);
    try {
      const resp = await fetch(href, { signal: ctrl.signal, mode: "cors" });
      if (!resp.ok) return null;
      const blob = await resp.blob();
      if (blob.size > 8 * 1024 * 1024 || !blob.type.startsWith("image/")) return null;
      dataUrl = await blobToDataUrl(blob);
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  } else {
    return null; // relative paths have no base to resolve against
  }
  const img = await loadImage(dataUrl);
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  if (!width || !height) return null;
  const mime = /^data:(image\/(?:png|jpeg));/i.exec(dataUrl)?.[1];
  if (!mime) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.min(width, 2400);
    canvas.height = Math.round((canvas.width * height) / width);
    canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/png"), width: canvas.width, height: canvas.height };
  }
  return { dataUrl, width, height };
}

// ───────────────────────── stored theme ─────────────────────────

interface ThemeChoice {
  presetId: string;
  config: MdThemeConfig;
  dirty: boolean;
}

function defaultChoice(): ThemeChoice {
  const preset = MD_THEMES[0]!;
  return { presetId: preset.id, config: cloneTheme(preset.config), dirty: false };
}

function loadStoredChoice(): ThemeChoice {
  try {
    const raw = localStorage.getItem(THEME_STORE_KEY);
    if (!raw) return defaultChoice();
    const parsed = JSON.parse(raw) as Partial<ThemeChoice>;
    const c = parsed.config;
    if (
      !c ||
      typeof c !== "object" ||
      !c.page ||
      !c.body ||
      !Array.isArray(c.headings) ||
      c.headings.length !== 6 ||
      !c.code ||
      !c.codespan ||
      !c.blockquote ||
      !c.list ||
      !c.table ||
      !c.hr ||
      !c.link ||
      !c.image
    ) {
      return defaultChoice();
    }
    return {
      presetId: typeof parsed.presetId === "string" ? parsed.presetId : "clean",
      config: c as MdThemeConfig,
      dirty: parsed.dirty === true,
    };
  } catch {
    return defaultChoice();
  }
}

/** Theme gallery card — a CSS caricature of the preset. */
function ThemeCard({
  preset,
  active,
  onPick,
}: {
  preset: (typeof MD_THEMES)[number];
  active: boolean;
  onPick: () => void;
}) {
  const c = preset.config;
  const h1 = c.headings[0];
  return (
    <button
      type="button"
      onClick={onPick}
      title={preset.blurb}
      className={`flex flex-col gap-1 rounded-md border p-1.5 text-left ${
        active
          ? "border-[color:var(--color-accent)] ring-1 ring-[color:var(--color-accent)]"
          : "border-[color:var(--color-border)] hover:border-[color:var(--color-border-strong)]"
      }`}
    >
      <div
        className="flex h-20 w-full flex-col gap-1 overflow-hidden rounded-sm border border-[color:var(--color-border)] p-1.5"
        style={{ background: c.page.background }}
      >
        <span
          className="truncate leading-none"
          style={{
            color: h1.color,
            fontFamily: familyCss(h1.family),
            fontWeight: h1.bold ? 700 : 400,
            fontStyle: h1.italic ? "italic" : "normal",
            fontSize: 11,
          }}
        >
          Heading
        </span>
        {h1.rule && <span className="h-px w-full" style={{ background: h1.ruleColor }} />}
        {[0.95, 0.8, 0.6].map((w, i) => (
          <span
            key={i}
            className="h-[3px] rounded-xs"
            style={{ width: `${w * 100}%`, background: c.body.color, opacity: 0.45 }}
          />
        ))}
        <span
          className="mt-auto h-[10px] w-3/5 rounded-xs border"
          style={{ background: c.code.background, borderColor: c.code.borderColor ?? c.code.background }}
        />
      </div>
      <span className="text-[11px] font-medium leading-tight">{preset.label}</span>
    </button>
  );
}

// ───────────────────────── the view ─────────────────────────

export function ImportMdView({
  name,
  markdown,
  customFonts,
  title = "Import Markdown",
  icon = "ri-markdown-line",
  notes = [],
  build,
  renderPage,
  commit,
  onCancel,
  onCommit,
}: {
  name: string;
  markdown: string;
  customFonts: CustomFont[];
  /** Header label + icon — Word and HTML imports reuse this view once
   *  they've been converted to markdown. */
  title?: string;
  icon?: string;
  /** Conversion warnings to surface (unsupported Word styles…). */
  notes?: string[];
  build: (args: {
    tokens: unknown[];
    theme: MdThemeConfig;
    faces: MdFace[];
    images: Record<string, MdImageResolved>;
  }) => Promise<{ pages: PageInfo[]; missingImages: string[] }>;
  renderPage: (pageIndex: number, scale: number) => Promise<RenderResult>;
  commit: () => Promise<ArrayBuffer>;
  onCancel: () => void;
  onCommit: (bytes: ArrayBuffer, pageCount: number) => void;
}) {
  const tokens = useMemo(() => [...marked.lexer(markdown)], [markdown]);

  // ── theme state (restored from the last import) ──
  const [choice, setChoice] = useState<ThemeChoice>(loadStoredChoice);
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORE_KEY, JSON.stringify(choice));
    } catch {
      /* storage full/blocked — theme just won't persist */
    }
  }, [choice]);

  const config = choice.config;
  const update = useCallback((fn: (c: MdThemeConfig) => void) => {
    setChoice((prev) => {
      const next = cloneTheme(prev.config);
      fn(next);
      return { ...prev, config: next, dirty: true };
    });
  }, []);
  const pickPreset = useCallback((id: string) => {
    const preset = MD_THEMES.find((p) => p.id === id);
    if (!preset) return;
    setChoice({ presetId: id, config: cloneTheme(preset.config), dirty: false });
  }, []);

  // ── images (resolved once per document) ──
  const [images, setImages] = useState<Record<string, MdImageResolved> | null>(null);
  useEffect(() => {
    let dead = false;
    const hrefs = new Set<string>();
    collectImageHrefs(tokens, hrefs);
    void (async () => {
      const out: Record<string, MdImageResolved> = {};
      for (const href of [...hrefs].slice(0, MAX_IMAGES)) {
        try {
          const resolved = await resolveImage(href);
          if (resolved) out[href] = resolved;
        } catch {
          /* placeholder path covers it */
        }
        if (dead) return;
      }
      if (!dead) setImages(out);
    })();
    return () => {
      dead = true;
    };
  }, [tokens]);

  // ── build loop (debounced; generation-guarded) ──
  const [pages, setPages] = useState<PageInfo[] | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [renderGen, setRenderGen] = useState(0);
  const [busy, setBusy] = useState(true);
  const [buildError, setBuildError] = useState<string | null>(null);
  const genRef = useRef(0);
  const hasBuiltRef = useRef(false);

  useEffect(() => {
    if (images === null) return;
    const gen = (genRef.current += 1);
    setBusy(true);
    const timer = setTimeout(
      () => {
        void (async () => {
          try {
            const faces = await resolveFaces(themeFamilies(config), customFonts);
            if (genRef.current !== gen) return;
            const res = await build({ tokens, theme: config, faces, images });
            if (genRef.current !== gen) return;
            hasBuiltRef.current = true;
            setPages(res.pages);
            setMissing(res.missingImages);
            setRenderGen(gen);
            setBuildError(null);
          } catch (err) {
            if (genRef.current === gen) {
              setBuildError(err instanceof Error ? err.message : String(err));
            }
          } finally {
            if (genRef.current === gen) setBusy(false);
          }
        })();
      },
      hasBuiltRef.current ? 250 : 0
    );
    return () => clearTimeout(timer);
  }, [tokens, config, images, customFonts, build]);

  // ── preview zoom ──
  const previewRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<"fit" | number>("fit");
  const [previewW, setPreviewW] = useState(800);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setPreviewW(el.clientWidth));
    ro.observe(el);
    setPreviewW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const pageW = pages?.[0]?.width ?? PAGE_PRESETS.A4![0];
  const scale =
    zoom === "fit" ? Math.max(0.2, Math.min(1.6, (previewW - 64) / pageW)) : zoom;

  // ── commit ──
  const [committing, setCommitting] = useState(false);
  const doImport = useCallback(() => {
    if (busy || committing || !pages || pages.length === 0) return;
    setCommitting(true);
    void (async () => {
      try {
        const bytes = await commit();
        onCommit(bytes, pages.length);
      } catch (err) {
        setCommitting(false);
        setBuildError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [busy, committing, pages, commit, onCommit]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  // ── customisation sections ──
  const [openSection, setOpenSection] = useState<string | null>(null);
  const toggle = (id: string) => setOpenSection((s) => (s === id ? null : id));
  const [headingLevel, setHeadingLevel] = useState(0);
  const h = config.headings[headingLevel]!;
  const setH = (fn: (h: MdHeadingStyle) => void) =>
    update((c) => fn(c.headings[headingLevel]!));

  const mm = (pt: number) => Math.round(pt / MM_TO_PT);

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-[color:var(--color-bg)]">
      {/* header */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[color:var(--color-border)] px-3 py-2">
        <i className={`${icon} text-lg text-[color:var(--color-fg-muted)]`} aria-hidden />
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="min-w-0 truncate text-xs text-[color:var(--color-fg-muted)]">{name}</span>
        <span className="text-xs text-[color:var(--color-fg-muted)]">
          {pages === null
            ? "building…"
            : `${pages.length} page${pages.length === 1 ? "" : "s"}`}
          {busy && pages !== null && " · updating…"}
        </span>
        {missing.length > 0 && (
          <span
            className="text-xs text-amber-600"
            title={missing.join("\n")}
          >
            <i className="ri-image-line" aria-hidden /> {missing.length} image
            {missing.length === 1 ? "" : "s"} unavailable
          </span>
        )}
        {notes.length > 0 && (
          <span className="text-xs text-amber-600" title={notes.join("\n")}>
            <i className="ri-information-line" aria-hidden /> {notes.length} conversion
            note{notes.length === 1 ? "" : "s"}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={typeof zoom === "number" ? String(zoom) : "fit"}
            onChange={(e) =>
              setZoom(e.target.value === "fit" ? "fit" : Number(e.target.value))
            }
            className="h-7 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-xs"
            title="Preview zoom"
          >
            <option value="fit">Fit width</option>
            <option value="0.75">75%</option>
            <option value="1">100%</option>
            <option value="1.25">125%</option>
            <option value="1.5">150%</option>
          </select>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || committing || !pages || pages.length === 0}
            onClick={doImport}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-40"
          >
            <i className="ri-file-transfer-line" aria-hidden />
            {committing ? "Importing…" : "Import as PDF"}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* sidebar: themes + customisation */}
        <div className="flex w-80 shrink-0 flex-col gap-3 overflow-y-auto border-r border-[color:var(--color-border)] p-3">
          <div>
            <div className="mb-1.5 flex items-center">
              <p className="text-xs font-medium">Theme</p>
              {choice.dirty && (
                <button
                  type="button"
                  onClick={() => pickPreset(choice.presetId)}
                  className="ml-auto text-[11px] text-[color:var(--color-accent)] hover:underline"
                  title="Discard customisations and restore the preset"
                >
                  Reset customisations
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {MD_THEMES.map((preset) => (
                <ThemeCard
                  key={preset.id}
                  preset={preset}
                  active={choice.presetId === preset.id}
                  onPick={() => pickPreset(preset.id)}
                />
              ))}
            </div>
          </div>

          <p className="text-xs font-medium">
            Customise
            {choice.dirty && (
              <span className="ml-1 font-normal text-[color:var(--color-fg-muted)]">(edited)</span>
            )}
          </p>

          <Section title="Page" open={openSection === "page"} onToggle={() => toggle("page")}>
            <Row label="Size">
              <select
                value={config.page.preset}
                onChange={(e) => update((c) => (c.page.preset = e.target.value as never))}
                className={`${fieldCls} flex-1`}
              >
                {Object.keys(PAGE_PRESETS).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <select
                value={config.page.landscape ? "landscape" : "portrait"}
                onChange={(e) => update((c) => (c.page.landscape = e.target.value === "landscape"))}
                className={`${fieldCls} flex-1`}
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </Row>
            <Row label="Margins mm">
              <Num
                title="Top / bottom"
                value={mm(config.page.marginTop)}
                min={5}
                max={60}
                step={1}
                onChange={(v) =>
                  update((c) => {
                    c.page.marginTop = v * MM_TO_PT;
                    c.page.marginBottom = v * MM_TO_PT;
                  })
                }
              />
              <Num
                title="Left / right"
                value={mm(config.page.marginLeft)}
                min={5}
                max={60}
                step={1}
                onChange={(v) =>
                  update((c) => {
                    c.page.marginLeft = v * MM_TO_PT;
                    c.page.marginRight = v * MM_TO_PT;
                  })
                }
              />
            </Row>
            <Row label="Background">
              <ColorPicker
                value={config.page.background}
                onChange={(v) => update((c) => (c.page.background = v))}
                title="Page background"
              />
            </Row>
          </Section>

          <Section title="Body text" open={openSection === "body"} onToggle={() => toggle("body")}>
            <Row label="Font">
              <FontSelect
                value={config.body.family}
                onChange={(v) => update((c) => (c.body.family = v))}
                customFonts={customFonts}
              />
            </Row>
            <Row label="Size / colour">
              <Num value={config.body.size} min={6} max={24} onChange={(v) => update((c) => (c.body.size = v))} />
              <ColorPicker
                value={config.body.color}
                onChange={(v) => update((c) => (c.body.color = v))}
                title="Body text colour"
              />
            </Row>
            <Row label="Line height">
              <Num
                value={config.body.lineHeight}
                min={1}
                max={2.5}
                step={0.05}
                onChange={(v) => update((c) => (c.body.lineHeight = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">× size</span>
            </Row>
            <Row label="Align">
              <AlignSelect
                value={config.body.align}
                onChange={(v) => update((c) => (c.body.align = v))}
                justify
              />
            </Row>
            <Row label="Para gap">
              <Num
                value={config.body.spaceAfter}
                min={0}
                max={30}
                onChange={(v) => update((c) => (c.body.spaceAfter = v))}
                title="Space after each paragraph, pt"
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">pt</span>
            </Row>
          </Section>

          <Section title="Headings" open={openSection === "headings"} onToggle={() => toggle("headings")}>
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4, 5].map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  onClick={() => setHeadingLevel(lvl)}
                  className={`inline-flex h-6 flex-1 items-center justify-center rounded-md border text-[11px] ${
                    headingLevel === lvl
                      ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
                      : "border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-subtle)]"
                  }`}
                >
                  H{lvl + 1}
                </button>
              ))}
            </div>
            <Row label="Font">
              <FontSelect value={h.family} onChange={(v) => setH((s) => (s.family = v))} customFonts={customFonts} />
            </Row>
            <Row label="Size / colour">
              <Num value={h.size} min={7} max={64} onChange={(v) => setH((s) => (s.size = v))} />
              <ColorPicker value={h.color} onChange={(v) => setH((s) => (s.color = v))} title="Heading colour" />
              <StyleToggles
                bold={h.bold}
                italic={h.italic}
                onBold={(v) => setH((s) => (s.bold = v))}
                onItalic={(v) => setH((s) => (s.italic = v))}
              />
            </Row>
            <Row label="Align">
              <AlignSelect value={h.align} onChange={(v) => setH((s) => (s.align = v))} />
            </Row>
            <Row label="Space pt">
              <Num
                title="Before"
                value={h.spaceBefore}
                min={0}
                max={60}
                step={1}
                onChange={(v) => setH((s) => (s.spaceBefore = v))}
              />
              <Num
                title="After"
                value={h.spaceAfter}
                min={0}
                max={60}
                step={1}
                onChange={(v) => setH((s) => (s.spaceAfter = v))}
              />
            </Row>
            <Row label="Rule below">
              <input
                type="checkbox"
                checked={h.rule}
                onChange={(e) => setH((s) => (s.rule = e.target.checked))}
              />
              {h.rule && (
                <ColorPicker value={h.ruleColor} onChange={(v) => setH((s) => (s.ruleColor = v))} title="Rule colour" />
              )}
            </Row>
          </Section>

          <Section title="Code blocks" open={openSection === "code"} onToggle={() => toggle("code")}>
            <Row label="Font">
              <FontSelect
                value={config.code.family}
                onChange={(v) => update((c) => (c.code.family = v))}
                customFonts={customFonts}
              />
            </Row>
            <Row label="Size / colour">
              <Num value={config.code.size} min={5} max={18} onChange={(v) => update((c) => (c.code.size = v))} />
              <ColorPicker
                value={config.code.color}
                onChange={(v) => update((c) => (c.code.color = v))}
                title="Code text colour"
              />
            </Row>
            <Row label="Background">
              <ColorPicker
                value={config.code.background}
                onChange={(v) => update((c) => (c.code.background = v))}
                title="Code block background"
              />
            </Row>
            <Row label="Border">
              <input
                type="checkbox"
                checked={config.code.borderColor !== null}
                onChange={(e) =>
                  update((c) => (c.code.borderColor = e.target.checked ? "#d0d7de" : null))
                }
              />
              {config.code.borderColor !== null && (
                <ColorPicker
                  value={config.code.borderColor}
                  onChange={(v) => update((c) => (c.code.borderColor = v))}
                  title="Code block border colour"
                />
              )}
            </Row>
            <Row label="Padding">
              <Num
                value={config.code.padding}
                min={0}
                max={30}
                step={1}
                onChange={(v) => update((c) => (c.code.padding = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">pt</span>
            </Row>
          </Section>

          <Section title="Inline code" open={openSection === "codespan"} onToggle={() => toggle("codespan")}>
            <Row label="Font">
              <FontSelect
                value={config.codespan.family}
                onChange={(v) => update((c) => (c.codespan.family = v))}
                customFonts={customFonts}
              />
            </Row>
            <Row label="Colour">
              <ColorPicker
                value={config.codespan.color}
                onChange={(v) => update((c) => (c.codespan.color = v))}
                title="Inline code colour"
              />
            </Row>
            <Row label="Background">
              <input
                type="checkbox"
                checked={config.codespan.background !== null}
                onChange={(e) =>
                  update((c) => (c.codespan.background = e.target.checked ? "#f6f8fa" : null))
                }
              />
              {config.codespan.background !== null && (
                <ColorPicker
                  value={config.codespan.background}
                  onChange={(v) => update((c) => (c.codespan.background = v))}
                  title="Inline code background"
                />
              )}
            </Row>
          </Section>

          <Section title="Quotes" open={openSection === "quote"} onToggle={() => toggle("quote")}>
            <Row label="Bar">
              <ColorPicker
                value={config.blockquote.barColor}
                onChange={(v) => update((c) => (c.blockquote.barColor = v))}
                title="Quote bar colour"
              />
              <Num
                value={config.blockquote.barWidth}
                min={0}
                max={10}
                onChange={(v) => update((c) => (c.blockquote.barWidth = v))}
                title="Bar thickness, pt"
              />
            </Row>
            <Row label="Text colour">
              <ColorPicker
                value={config.blockquote.color}
                onChange={(v) => update((c) => (c.blockquote.color = v))}
                title="Quoted text colour"
              />
              <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                <input
                  type="checkbox"
                  checked={config.blockquote.italic}
                  onChange={(e) => update((c) => (c.blockquote.italic = e.target.checked))}
                />
                Italic
              </label>
            </Row>
            <Row label="Background">
              <input
                type="checkbox"
                checked={config.blockquote.background !== null}
                onChange={(e) =>
                  update((c) => (c.blockquote.background = e.target.checked ? "#f6f8fa" : null))
                }
              />
              {config.blockquote.background !== null && (
                <ColorPicker
                  value={config.blockquote.background}
                  onChange={(v) => update((c) => (c.blockquote.background = v))}
                  title="Quote background"
                />
              )}
            </Row>
            <Row label="Indent">
              <Num
                value={config.blockquote.indent}
                min={4}
                max={40}
                step={1}
                onChange={(v) => update((c) => (c.blockquote.indent = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">pt</span>
            </Row>
          </Section>

          <Section title="Lists" open={openSection === "list"} onToggle={() => toggle("list")}>
            <Row label="Bullets">
              <select
                value={config.list.bullets}
                onChange={(e) => update((c) => (c.list.bullets = e.target.value))}
                className={`${fieldCls} flex-1`}
              >
                <option value="•◦▪">• ◦ ▪</option>
                <option value="•">• • •</option>
                <option value="–">– – –</option>
                <option value="»›-">» › -</option>
                <option value="*+-">* + -</option>
              </select>
            </Row>
            <Row label="Marker">
              <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                <input
                  type="checkbox"
                  checked={config.list.markerColor !== ""}
                  onChange={(e) =>
                    update((c) => (c.list.markerColor = e.target.checked ? c.body.color : ""))
                  }
                />
                Custom colour
              </label>
              {config.list.markerColor !== "" && (
                <ColorPicker
                  value={config.list.markerColor}
                  onChange={(v) => update((c) => (c.list.markerColor = v))}
                  title="Marker colour"
                />
              )}
            </Row>
            <Row label="Indent">
              <Num
                value={config.list.indent}
                min={10}
                max={40}
                step={1}
                onChange={(v) => update((c) => (c.list.indent = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">pt / level</span>
            </Row>
            <Row label="Item gap">
              <Num
                value={config.list.itemGap}
                min={0}
                max={12}
                onChange={(v) => update((c) => (c.list.itemGap = v))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">pt</span>
            </Row>
          </Section>

          <Section title="Tables" open={openSection === "table"} onToggle={() => toggle("table")}>
            <Row label="Font size">
              <Num value={config.table.size} min={5} max={16} onChange={(v) => update((c) => (c.table.size = v))} />
            </Row>
            <Row label="Borders">
              <ColorPicker
                value={config.table.borderColor}
                onChange={(v) => update((c) => (c.table.borderColor = v))}
                title="Border colour"
              />
              <Num
                value={config.table.borderWidth}
                min={0.25}
                max={3}
                step={0.25}
                onChange={(v) => update((c) => (c.table.borderWidth = v))}
                title="Border thickness, pt"
              />
            </Row>
            <Row label="Header">
              <ColorPicker
                value={config.table.headerBackground}
                onChange={(v) => update((c) => (c.table.headerBackground = v))}
                title="Header background"
              />
              <ColorPicker
                value={config.table.headerColor}
                onChange={(v) => update((c) => (c.table.headerColor = v))}
                title="Header text colour"
              />
              <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                <input
                  type="checkbox"
                  checked={config.table.headerBold}
                  onChange={(e) => update((c) => (c.table.headerBold = e.target.checked))}
                />
                Bold
              </label>
            </Row>
            <Row label="Stripes">
              <input
                type="checkbox"
                checked={config.table.stripeBackground !== null}
                onChange={(e) =>
                  update((c) => (c.table.stripeBackground = e.target.checked ? "#f6f8fa" : null))
                }
              />
              {config.table.stripeBackground !== null && (
                <ColorPicker
                  value={config.table.stripeBackground}
                  onChange={(v) => update((c) => (c.table.stripeBackground = v))}
                  title="Stripe background"
                />
              )}
            </Row>
            <Row label="Cell pad">
              <Num
                title="Horizontal"
                value={config.table.cellPaddingX}
                min={1}
                max={20}
                step={1}
                onChange={(v) => update((c) => (c.table.cellPaddingX = v))}
              />
              <Num
                title="Vertical"
                value={config.table.cellPaddingY}
                min={1}
                max={20}
                step={1}
                onChange={(v) => update((c) => (c.table.cellPaddingY = v))}
              />
            </Row>
          </Section>

          <Section title="Links" open={openSection === "link"} onToggle={() => toggle("link")}>
            <Row label="Colour">
              <ColorPicker
                value={config.link.color}
                onChange={(v) => update((c) => (c.link.color = v))}
                title="Link colour"
              />
              <label className="inline-flex cursor-pointer items-center gap-1 text-[11px]">
                <input
                  type="checkbox"
                  checked={config.link.underline}
                  onChange={(e) => update((c) => (c.link.underline = e.target.checked))}
                />
                Underline
              </label>
            </Row>
          </Section>

          <Section title="Dividers (---)" open={openSection === "hr"} onToggle={() => toggle("hr")}>
            <Row label="Colour">
              <ColorPicker
                value={config.hr.color}
                onChange={(v) => update((c) => (c.hr.color = v))}
                title="Divider colour"
              />
              <Num
                value={config.hr.width}
                min={0.25}
                max={6}
                step={0.25}
                onChange={(v) => update((c) => (c.hr.width = v))}
                title="Thickness, pt"
              />
            </Row>
          </Section>

          <Section title="Images" open={openSection === "image"} onToggle={() => toggle("image")}>
            <Row label="Align">
              <select
                value={config.image.align}
                onChange={(e) => update((c) => (c.image.align = e.target.value as never))}
                className={`${fieldCls} flex-1`}
              >
                <option value="center">Centre</option>
                <option value="left">Left</option>
              </select>
            </Row>
            <Row label="Max width">
              <Num
                value={Math.round(config.image.maxWidth * 100)}
                min={10}
                max={100}
                step={5}
                onChange={(v) => update((c) => (c.image.maxWidth = v / 100))}
              />
              <span className="text-[10px] text-[color:var(--color-fg-muted)]">% of column</span>
            </Row>
          </Section>

          <p className="text-[10px] text-[color:var(--color-fg-muted)]">
            Headings become PDF bookmarks; links stay clickable. The preview is the
            generated PDF itself — importing keeps it exactly as shown.
          </p>
        </div>

        {/* preview */}
        <div ref={previewRef} className="min-w-0 flex-1 overflow-auto bg-[color:var(--color-bg-subtle)]">
          {buildError ? (
            <div className="p-6 text-sm text-red-600">Could not build the preview: {buildError}</div>
          ) : pages === null ? (
            <div className="p-6 text-sm text-[color:var(--color-fg-muted)]">
              <i className="ri-loader-4-line animate-spin" aria-hidden /> Building preview…
            </div>
          ) : (
            <div className="flex flex-col items-center gap-7 p-6 pb-10">
              {pages.map((info, i) => (
                <PreviewPage
                  key={i}
                  pageIndex={i}
                  info={info}
                  scale={scale}
                  gen={renderGen}
                  renderPage={renderPage}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
